"use client"

import type React from "react"
import { useEffect, useState, useRef } from "react"
import { X, Plus, Upload, ImageIcon } from "lucide-react"
import { Button } from "../../../components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import { Textarea } from "../../../components/ui/textarea"
import type { Kit } from "./categories"

type KitModalProps = {
  isOpen: boolean
  onClose: () => void
  onSave: (kit: Omit<Kit, "id" | "categoryId">) => void
  initialValue?: Kit | null
}

export function KitModal({ isOpen, onClose, onSave, initialValue }: KitModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    thumb: "",
    price1: "",
    price2: "",
    price3: "",
    description: "",
    features: [""],
    path: ""
  })
  const [imagePreview, setImagePreview] = useState<string>("")
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen) {
      if (initialValue) {
        setFormData({
          name: initialValue.name,
          thumb: initialValue.thumb,
          price1: initialValue.price1.toString(),
          price2: initialValue.price2.toString(),
          price3: initialValue.price3.toString(),
          description: initialValue.description,
          features: initialValue.features.length > 0 ? initialValue.features : [""],
          path: initialValue.path
        })
        setImagePreview(initialValue.thumb)
      } else {
        setFormData({
          name: "",
          thumb: "",
          price1: "",
          price2: "",
          price3: "",
          description: "",
          features: [""],
          path: ""
        })
        setImagePreview("")
      }
    }
  }, [isOpen, initialValue])

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        const base64String = reader.result as string
        setFormData({ ...formData, thumb: base64String })
        setImagePreview(base64String)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (formData.name.trim() && formData.price1 && formData.price2 && formData.price3) {
      onSave({
        name: formData.name.trim(),
        thumb: formData.thumb,
        price1: Number.parseFloat(formData.price1),
        price2: Number.parseFloat(formData.price2),
        price3: Number.parseFloat(formData.price3),
        description: formData.description.trim(),
        features: formData.features.filter((f) => f.trim() !== ""),
        path: formData.path
      })
    }
  }

  const addFeature = () => {
    setFormData({ ...formData, features: [...formData.features, ""] })
  }

  const updateFeature = (index: number, value: string) => {
    const newFeatures = [...formData.features]
    newFeatures[index] = value
    setFormData({ ...formData, features: newFeatures })
  }

  const removeFeature = (index: number) => {
    setFormData({ ...formData, features: formData.features.filter((_, i) => i !== index) })
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-card sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-foreground">{initialValue ? "Editar Kit" : "Novo Kit"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="name" className="text-foreground">
                  Nome do Kit
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Kit Completo Pro"
                  className="bg-background text-foreground"
                  autoFocus
                />
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label className="text-foreground">Imagem do Kit</Label>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full gap-2 bg-background"
                    >
                      <Upload className="h-4 w-4" />
                      {imagePreview ? "Alterar Imagem" : "Fazer Upload"}
                    </Button>
                  </div>
                  {imagePreview && (
                    <div className="relative h-20 w-20 overflow-hidden rounded-lg border border-border bg-muted">
                      <img
                        src={imagePreview || "/placeholder.svg"}
                        alt="Preview"
                        className="h-full w-full object-cover"
                      />
                    </div>
                  )}
                  {!imagePreview && (
                    <div className="flex h-20 w-20 items-center justify-center rounded-lg border border-dashed border-border bg-muted">
                      <ImageIcon className="h-8 w-8 text-muted-foreground" />
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="price1" className="text-foreground">
                  Camisa
                </Label>
                <Input
                  id="price1"
                  type="number"
                  step="0.01"
                  value={formData.price1}
                  onChange={(e) => setFormData({ ...formData, price1: e.target.value })}
                  placeholder="0.00"
                  className="bg-background text-foreground"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="price2" className="text-foreground">
                  Short
                </Label>
                <Input
                  id="price2"
                  type="number"
                  step="0.01"
                  value={formData.price2}
                  onChange={(e) => setFormData({ ...formData, price2: e.target.value })}
                  placeholder="0.00"
                  className="bg-background text-foreground"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="price3" className="text-foreground">
                  Meião
                </Label>
                <Input
                  id="price3"
                  type="number"
                  step="0.01"
                  value={formData.price3}
                  onChange={(e) => setFormData({ ...formData, price3: e.target.value })}
                  placeholder="0.00"
                  className="bg-background text-foreground"
                />
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="description" className="text-foreground">
                  Descrição
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descreva o kit..."
                  className="min-h-20 bg-background text-foreground"
                />
              </div>

              <div className="space-y-2 sm:col-span-2">
                <div className="flex items-center justify-between">
                  <Label className="text-foreground">Características</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addFeature}
                    className="gap-1 bg-transparent"
                  >
                    <Plus className="h-3 w-3" />
                    Adicionar
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={feature}
                        onChange={(e) => updateFeature(index, e.target.value)}
                        placeholder={`Característica ${index + 1}`}
                        className="bg-background text-foreground"
                      />
                      {formData.features.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFeature(index)}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!formData.name.trim() || !formData.price1 || !formData.price2 || !formData.price3}
              className="bg-primary text-primary-foreground"
            >
              {initialValue ? "Salvar" : "Adicionar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}